/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with utc plugin
dayjs.extend(utc);
dayjs.extend(timezone);

// components imports
import { Card, Col, ColProps, Row, Skeleton } from "antd";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
} from "recharts";
import { IAnalytics, ITotalHits } from "@/interfaces/Analytics.interface";
import { getFormattedDate } from "@/utils/date/getFormattedDate";

/**
 * Props for the custom tooltip component.
 */
interface IRechartsTooltipProps {
  active?: boolean;
  payload?: Record<string, any>;
  label?: string;
  userTimezone?: string;
}

const CHART_COLORS = {
  hit: "#20c76b",
  miss: "#a88cd9",
  deny: "#ca829e",
};

/**
 * Custom tooltip component for Recharts.
 */
const CustomTooltip = ({
  payload,
  label,
  userTimezone,
}: IRechartsTooltipProps) => {
  const { t } = useTranslation();

  return (
    <div className="custom-tooltip">
      <p className="label">{`${getFormattedDate(label, userTimezone)}`}</p>
      {payload?.map((entry: any) => (
        <span key={entry.dataKey}>
          <span style={{ color: entry.color }}>
            {t(`analytics.${entry.dataKey}`)}: {entry.payload[entry.dataKey]}
          </span>
          <br />
        </span>
      ))}
    </div>
  );
};

interface ColChartProps extends ColProps {
  title: string;
  dataKey1: string;
  dataKey2: string;
  dataKey3: string;
  hitsData: ITotalHits[];
  tickFormatter: (value: any, index: number) => string;
  userTimezone: string | undefined;
}

/**
 * Chart component for a single column.
 */
const ColChart = ({
  title,
  dataKey1,
  dataKey2,
  dataKey3,
  hitsData,
  tickFormatter,
  userTimezone,
  ...rest
}: ColChartProps) => {
  const { t } = useTranslation();
  return (
    <Col {...rest}>
      <Card className="mb-5" title={title}>
        {hitsData.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={hitsData}
              margin={{
                top: 5,
                right: 35,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                style={{ fontSize: ".75rem" }}
                dataKey="analytic_date"
                tickCount={5}
                tickFormatter={tickFormatter}
                angle={-25}
                dx={0}
                dy={20}
              />
              <YAxis style={{ fontSize: ".75rem" }} />
              <Tooltip
                content={<CustomTooltip userTimezone={userTimezone} />}
              />
              <Legend
                formatter={(value) => t(`analytics.${value}`)}
                verticalAlign="top"
              />
              <Line
                type="monotone"
                dataKey={dataKey1}
                stroke={CHART_COLORS.hit}
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey={dataKey2}
                stroke={CHART_COLORS.miss}
              />
              <Line
                type="monotone"
                dataKey={dataKey3}
                stroke={CHART_COLORS.deny}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div
            style={{
              display: "flex",
              flexFlow: "column",
              justifyContent: "center",
              alignItems: "center",
              background: "#ECECEC",
              width: "100%",
              minHeight: 300,
              marginLeft: 15,
              marginRight: 15,
            }}
          >
            <Skeleton.Image />
            <p>{t("common.loading")}</p>
          </div>
        )}
      </Card>
    </Col>
  );
};

/**
 * Main component for displaying analytics charts.
 */
export const AnalyticsCharts = ({
  analyticsData,
  userTimezone,
  rangeToBeHourly,
}: {
  analyticsData: IAnalytics[] | undefined;
  userTimezone: string | undefined;
  rangeToBeHourly: boolean;
}) => {
  const { t } = useTranslation();

  // Get total hits data
  const hitsObject = analyticsData?.find((item) => item.id === "total_hits");
  const hitsData = (hitsObject?.data || []) as ITotalHits[];

  // Format date based on the specified conditions
  const formatDate = (date: string) => {
    if (rangeToBeHourly) {
      return dayjs(date).tz(userTimezone).format("HH:mm");
    }
    return dayjs(date).format("DD-MM-YYYY");
  };

  return (
    <Row gutter={16}>
      <ColChart
        xs={24}
        title={t("analytics.hits_misses_title")}
        dataKey1="total_hits"
        dataKey2="total_misses"
        dataKey3="total_denies"
        hitsData={hitsData}
        tickFormatter={formatDate}
        userTimezone={userTimezone}
      />
      <ColChart
        lg={12}
        xs={24}
        title={t("analytics.completion_tokens")}
        dataKey1="hits_completion_tokens"
        dataKey2="miss_completion_tokens"
        dataKey3="deny_completion_tokens"
        hitsData={hitsData}
        tickFormatter={formatDate}
        userTimezone={userTimezone}
      />
      <ColChart
        lg={12}
        xs={24}
        title={t("analytics.prompt_tokens")}
        dataKey1="hits_prompt_tokens"
        dataKey2="miss_prompt_tokens"
        dataKey3="deny_prompt_tokens"
        hitsData={hitsData}
        tickFormatter={formatDate}
        userTimezone={userTimezone}
      />
    </Row>
  );
};
